
import { processedUrl, proxiedUrl } from '@padlet/vivaldi-client'
import { attachmentLoadingAnimationColorTailwindClass } from 'bits/attachments'
import device from 'bits/device'
import overflowBorderRadiusTransformFixStyle from 'bits/overflow_border_radius_transform_fix'
import { postColorValues } from 'bits/post_color'
import { Component, Prop, Vue } from 'bits/vue'
import BluePurpleLanterns from 'images/blue_purple_lanterns.png'
import { sample } from 'lodash-es'
import type { PostColor } from 'types'
import BaseImage from 'vuecomponents/base_image.vue'

/**
 * Thumbnail that renders a colored placeholder while image is loading.
 * @displayName Image Thumbnail
 */

@Component({
  components: { BaseImage },
})
export default class ImageThumbnail extends Vue {
  loaded = false
  error = false
  isSafari = device?.safari
  errorPlaceholderImage = BluePurpleLanterns

  /**
   * URL for image
   */
  @Prop({ required: true }) src!: string

  /**
   * Original image width
   */
  @Prop({ required: true }) originalImageWidth!: number

  /**
   * Original image height
   */
  @Prop({ required: true }) originalImageHeight!: number

  /**
   * Resulting component width
   */
  @Prop({ required: true }) width!: number

  /**
   * Resulting component height. Ignored for calculation.
   * Only use to send to Vivaldi if processImage is true.
   */
  @Prop({ default: undefined }) height?: number

  /**
   * Resize strategy. Ignored if no aspect ratio is provided.
   * @values crop, stretch, fit
   */
  @Prop({ default: 'crop' }) resizeStrategy?: 'crop' | 'stretch' | 'fit'

  /**
   * If specified, thumbnail height will be computed based on this value.
   * Value should be `width / height`, i.e. `2` will be wide and `0.5` will be tall.
   */
  @Prop() aspectRatio?: number

  /**
   * Title to display when the image fails to load
   */
  @Prop({ default: '' }) title!: string

  /**
   * Determines if we should load the image lazily.
   */
  @Prop({ default: true }) lazyLoading!: boolean

  /**
   * Determines whether image should be proxied to vivaldi for caching
   */
  @Prop({ default: false }) proxyImage!: boolean

  /**
   * Determines whether image should be processed before loading
   */
  @Prop({ default: false }) processImage!: boolean

  /**
   * Placeholder color for the thumbnail.
   * @values red, blue, green, yellow, purple, pink, cyan, orange, transparent, random
   */
  @Prop({ default: 'transparent' }) placeholderColor!: string

  /**
   * Placeholder 'background-color' value.
   */
  @Prop() placeholderColorValue?: string

  /**
   * If true, display placeholder image when original image fails to load
   * Else, show image title on top of placeholder solid background.
   */
  @Prop({ default: false }) fallbackToPlaceholder!: boolean

  /**
   * If passed, will enable animation for the placeholder.
   */
  @Prop({ required: false }) placeholderAnimationVariant?: 'dark' | 'light'

  /**
   * data-cy
   */
  @Prop({ required: false }) dataCy?: string

  /**
   * Flip the image.
   */
  @Prop({ required: false }) flipDirection?: 'horizontal' | 'vertical'

  /**
   * Determines where the image position is when used in conjunction with object-fit
   * Else the image will always be cropped from the center.
   */
  @Prop({ required: false }) objectPosition?: string

  get overflowBorderRadiusTransformFixStyle(): Record<string, string> {
    return overflowBorderRadiusTransformFixStyle(this.isSafari)
  }

  get bgColorClass(): string {
    if (this.placeholderColorValue) return ''
    if (this.loaded) return ''

    // Currently, we only define pulsing animation for 6 post colors.
    // If `placeholderColor` isn't one of them, we just fallback to static colors.
    if (
      this.placeholderAnimationVariant &&
      // null is default color
      postColorValues.includes(this.placeholderColor === 'default' ? null : (this.placeholderColor as PostColor))
    ) {
      return attachmentLoadingAnimationColorTailwindClass({
        postColor: this.placeholderColor as PostColor,
        isLightColorScheme: this.placeholderAnimationVariant === 'light',
      })
    }

    const colorClassMap = {
      red: 'bg-bgi-red',
      blue: 'bg-bgi-blue',
      green: 'bg-bgi-green',
      yellow: 'bg-bgi-yellow',
      purple: 'bg-bgi-purple',
      pink: 'bg-bgi-pink',
      cyan: 'bg-bgi-cyan',
      orange: 'bg-bgi-orange',
      grey: 'bg-highway-200',
    }

    if (this.placeholderColor === 'random') {
      return sample(Object.values(colorClassMap)) ?? ''
    } else {
      // 'transparent' maps to an empty string
      return colorClassMap[this.placeholderColor] || ''
    }
  }

  get url(): string {
    if (this.processImage) {
      const processingOptions: { width: number; height?: number; ar?: number } = { width: this.width }
      if (this.height) {
        // Vivaldi has a bug that if you size an image using limit and fit strategies and not providing
        // height, the height will be the same as width.
        // See more here: https://github.com/padlet/mozart/pull/9484
        processingOptions.height = this.height
      }
      if (this.resizeStrategy === 'stretch' && this.aspectRatio) {
        processingOptions.ar = this.aspectRatio
      }
      // Vivaldi can also crop the image for us.
      // We don't request cropped images because we want to avoid spamming Vivaldi as the image is resized.
      // We can revisit this.
      return processedUrl(this.src, processingOptions)
    }
    return this.proxyImage ? proxiedUrl(this.src) : this.src
  }

  get originalImageAspectRatio(): number {
    return this.originalImageWidth / this.originalImageHeight
  }

  get dimensions(): {
    cropFrameWidth: number
    cropFrameWidthString: string
    cropFrameHeight: number
    cropFrameHeightString: string
    cropFrameAspectRatio: number
    imageElementWidth: number
    imageElementWidthString: string
    imageElementHeight: number
    imageElementHeightString: string
  } {
    // Width is fixed except when resizeStrategy === 'fit' (CASE 3)
    let cropFrameWidth = this.width
    let unit: 'px' | '%' = 'px'

    // These are decided based on the resizing strategy.
    let cropFrameHeight: number
    let cropFrameAspectRatio: number
    let imageElementHeight: number
    let imageElementWidth: number

    // CASE 1: Crop to fit aspect ratio
    if (this.resizeStrategy === 'crop' && this.aspectRatio) {
      // Try to fit as much of the image in the crop frame as possible.
      cropFrameAspectRatio = this.aspectRatio

      // All cropFrameHeight calculation in this method should use Math.ceil.
      // Reason:
      // Browsers can display HTML elements with fractional height.
      // In Vivaldi, when calculating height using width and aspect ratio, we use
      // Math.ceil (see [1] and [2]). So the resulting image height will never be
      // fractional. But the <div> in this component can have fractional height.
      // -> By using Math.ceil, we ensure the <div> will have the same height as
      // the image.
      //
      // [1]: https://github.com/padlet/mozart/blob/21a2867e9c2abf5e71db0bb6c3eaa6b86b6cd904/services/vivaldi/src/vips/fill.js#L30
      // [2]: https://github.com/padlet/mozart/blob/21a2867e9c2abf5e71db0bb6c3eaa6b86b6cd904/services/vivaldi/src/vips/limit_fill.js#L39
      cropFrameHeight = Math.ceil(cropFrameWidth / cropFrameAspectRatio)
      const originalHasWiderAspectRatio = this.originalImageAspectRatio > this.aspectRatio
      if (originalHasWiderAspectRatio) {
        imageElementHeight = cropFrameHeight
        imageElementWidth = this.originalImageAspectRatio * cropFrameHeight
      } else {
        imageElementWidth = cropFrameWidth
        imageElementHeight = imageElementWidth / this.originalImageAspectRatio
      }

      // CASE 2: Stretch to fit aspect ratio
    } else if (this.resizeStrategy === 'stretch' && this.aspectRatio) {
      // Image element and crop frame both have the same dimensions.
      cropFrameAspectRatio = this.aspectRatio
      cropFrameHeight = Math.ceil(cropFrameWidth / cropFrameAspectRatio)
      imageElementWidth = cropFrameWidth
      imageElementHeight = cropFrameHeight

      // CASE 3: Fit parent's dimensions
    } else if (this.resizeStrategy === 'fit') {
      // Fit the image to its parent element regardless of aspect ratio.
      cropFrameAspectRatio = this.originalImageAspectRatio
      cropFrameWidth = 100
      cropFrameHeight = 100
      imageElementWidth = 100
      imageElementHeight = 100
      unit = '%'

      // CASE 4: Scale, keeping original aspect ratio
    } else {
      // Maintain the same ratio as the original, but scale down proportionately if need be.
      cropFrameAspectRatio = this.originalImageAspectRatio
      cropFrameHeight = Math.ceil(cropFrameWidth / cropFrameAspectRatio)
      imageElementWidth = cropFrameWidth
      imageElementHeight = cropFrameHeight
    }

    return {
      cropFrameWidth,
      cropFrameWidthString: `${cropFrameWidth}${unit}`,
      cropFrameHeight,
      cropFrameHeightString: `${cropFrameHeight}${unit}`,
      cropFrameAspectRatio,
      imageElementWidth,
      imageElementWidthString: `${imageElementWidth}${unit}`,
      imageElementHeight,
      imageElementHeightString: `${imageElementHeight}${unit}`,
    }
  }

  onLoad(): void {
    this.loaded = true
    this.$emit('load')
  }

  onError(e: Error): void {
    this.error = true
    this.$emit('error', e)
  }
}
